import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge } from '~/components/ui/Badge';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';

export const Route = createFileRoute('/_admin/onboarding-slides/')({
  validateSearch: (s: Record<string, unknown>) => ({
    onboarding: Number(s.onboarding),
  }),
  component: OnboardingSlidesList,
});

type OnboardingSequenceSlideItem =
  components['schemas']['OnboardingSequenceSlideItem'];

const columnHelper = createColumnHelper<OnboardingSequenceSlideItem>();

const columns = [
  columnHelper.accessor('slide_index', { header: 'Index' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('layout', { header: 'Layout' }),
  columnHelper.accessor('enabled', {
    header: 'Enabled',
    cell: (info) => (
      <Badge $variant={info.getValue() ? 'success' : 'error'}>
        {info.getValue() ? 'Yes' : 'No'}
      </Badge>
    ),
  }),
];

function OnboardingSlidesList() {
  const { onboarding } = Route.useSearch();
  const { data, isLoading, error } = api.getOnboardingSequence.useQuery({
    path: { onboarding_id: onboarding },
  });

  const table = useReactTable({
    data: data?.slides ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <BackLink>
        <Link
          to="/onboardings/$onboardingId"
          params={{ onboardingId: String(onboarding) }}
        >
          &larr; Back to Onboarding #{onboarding}
        </Link>
      </BackLink>
      <PageTitle>Slides</PageTitle>
      {isLoading ? (
        <Message>Loading slides...</Message>
      ) : error ? (
        <Message $error>Error loading slides</Message>
      ) : (
        <Table
          table={table}
          to="/onboarding-slides/$onboardingId/$slideIndex"
          getParams={(row) => ({
            onboardingId: String(onboarding),
            slideIndex: String(row.slide_index),
          })}
        />
      )}
    </div>
  );
}

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
